import { isArray, isEmpty } from 'lodash';
import { ApiError, parseApiError, handleApiError } from './errors';
// Generate a random sessionId for the current session
//
// This will be calculated when the app is loaded, and will
// only be discarded when the user refreshes or closes the page
/**
 * Modified version of fetch that detects when a network error occurs,
 * and delays processing for a full second before moving on to handling the error.
 *
 * This is needed when the user refreshes the browser while a request to the
 * backend is still pending. When that happens, fetch rejects the request with
 * a `NetworkError` *on some browser*  (confirmed on desktop Firefox, iOS Chrome & Safari).
 *
 * In agent-ui, handling those errors meant to redirect the user to the agent app,
 * which basically canceled the page refresh that the user had issued, which was
 * obviously incorrect.
 */
const modifiedFetch = (input, init) => fetch(input, init).catch(async (err) => {
    // delay for 1s to give the browser time to reload the page
    await new Promise((resolve) => setTimeout(resolve, 1000));
    throw err;
});
const replacePathParams = (obj, openapiPath) => {
    const trimmedPath = openapiPath.charAt(0) === '/' ? openapiPath.substring(1) : openapiPath;
    const path = obj
        ? Object.keys(obj).reduce((acc, key) => acc.replace(`{${key}}`, obj[key]), trimmedPath)
        : trimmedPath;
    return path;
};
const RestClient = (clientConfigP) => ({
    get: async (params) => {
        const { prefixUrl, ...clientConfig } = await clientConfigP;
        const path = replacePathParams(params.pathParams, params.openapiPath);
        const searchParams = encodeSearchParams(params.queryParams);
        const newUrl = params.queryParams
            ? `${prefixUrl}/${path}?${searchParams}`
            : `${prefixUrl}/${path}`;
        const response = await modifiedFetch(newUrl, {
            ...clientConfig,
            method: 'GET',
            headers: {
                ...params.headers,
                'Content-Type': 'application/json',
            },
        });
        if (!response.ok) {
            throw await buildError({ method: 'GET', reqParams: params, response });
        }
        return {
            body: (await response.json()),
            headers: response.headers,
            statusCode: response.status,
        };
    },
    post: async (params) => {
        const clientConfig = await clientConfigP;
        const path = replacePathParams(params.pathParams, params.openapiPath);
        const searchParams = encodeSearchParams(params.queryParams);
        const newUrl = params.queryParams
            ? `${clientConfig?.prefixUrl}/${path}?${searchParams}`
            : `${clientConfig?.prefixUrl}/${path}`;
        const response = await modifiedFetch(newUrl, {
            ...clientConfig,
            method: 'POST',
            headers: {
                ...params.headers,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(params.requestBody),
        });
        if (!response.ok) {
            throw await buildError({ method: 'POST', reqParams: params, response });
        }
        return {
            body: (await response.json()),
            headers: response.headers,
            statusCode: response.status,
        };
    },
    put: async (params) => {
        const clientConfig = await clientConfigP;
        const path = replacePathParams(params.pathParams, params.openapiPath);
        const searchParams = encodeSearchParams(params.queryParams);
        const newUrl = params.queryParams
            ? `${clientConfig?.prefixUrl}/${path}?${searchParams}`
            : `${clientConfig?.prefixUrl}/${path}`;
        const response = await modifiedFetch(newUrl, {
            ...clientConfig,
            method: 'PUT',
            headers: {
                ...params.headers,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(params.requestBody),
        });
        if (!response.ok) {
            throw await buildError({ method: ' PUT', reqParams: params, response });
        }
        // Check the type of the response
        const contentType = response.headers.get('Content-Type');
        return {
            body: contentType && contentType.includes('application/json')
                ? (await response.json())
                : (await response.text()),
            headers: response.headers,
            statusCode: response.status,
        };
    },
    patch: async (params) => {
        const clientConfig = await clientConfigP;
        const path = replacePathParams(params.pathParams, params.openapiPath);
        const searchParams = encodeSearchParams(params.queryParams);
        const newUrl = params.queryParams
            ? `${clientConfig?.prefixUrl}/${path}?${searchParams}`
            : `${clientConfig?.prefixUrl}/${path}`;
        const response = await modifiedFetch(newUrl, {
            ...clientConfig,
            method: 'PATCH',
            headers: {
                ...params.headers,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(params.requestBody),
        });
        if (!response.ok) {
            throw await buildError({ method: 'PATCH', reqParams: params, response });
        }
        return {
            body: (await response.json()),
            headers: response.headers,
            statusCode: response.status,
        };
    },
    delete: async (params) => {
        const clientConfig = await clientConfigP;
        const path = replacePathParams(params.pathParams, params.openapiPath);
        const searchParams = encodeSearchParams(params.queryParams);
        const newUrl = params.queryParams
            ? `${clientConfig?.prefixUrl}/${path}?${searchParams}`
            : `${clientConfig?.prefixUrl}/${path}`;
        const response = await modifiedFetch(newUrl, {
            ...clientConfig,
            method: 'DELETE',
            headers: {
                ...params.headers,
            },
        });
        if (!response.ok) {
            throw await buildError({ method: 'DELETE', reqParams: params, response });
        }
        // Check the type of the response
        const contentType = response.headers.get('Content-Type');
        return {
            body: contentType && contentType.includes('application/json')
                ? (await response.json())
                : (await response.text()),
            headers: response.headers,
            statusCode: response.status,
        };
    },
});
/**
 * An `RestClient` instance where all parameters are `unknown`.
 * This is basically the same thins as using **got** directly, but has a few advantages:
 * - uses a call signature similar (equal) to `RestClient`
 * - rejections are immediately caught and transformed into `GotAppErrors`, and a descriptive stack trace is built.
 * - searc params are handled with `URLSearchParams` like in `RestClient`
 */
const UntypedRestClient = (clientConfig) => {
    const restClient = RestClient(clientConfig);
    return {
        get: restClient.get,
        post: restClient.post,
        put: restClient.put,
        patch: restClient.patch,
        delete: restClient.delete,
    };
};
const encodeSearchParams = (params) => {
    const searchParams = new URLSearchParams();
    if (isEmpty(params)) {
        return searchParams;
    }
    Object.keys(params).forEach((key) => {
        const value = params[key];
        if (isArray(value)) {
            value.forEach((v) => searchParams.append(key, v));
        }
        else if (value !== undefined) {
            searchParams.append(key, value);
        }
    });
    return searchParams;
};
/**
 * Take the fetch response and build a standard error response using ApiError
 */
const buildError = async (params) => {
    // Get the interactionId header from the response in order to include it in any error messages generated by the system.
    // Later used in Sentry reports and will be useful to correlate sentry and server logs
    const interactionId = params.response.headers.get('x-interaction-id');
    const statusCode = params.response.status;
    let apiError;
    try {
        const contentType = params.response.headers.get('Content-Type');
        if (contentType && contentType.includes('application/json')) {
            const responseBody = await params.response.json();
            // Parse the API error using the new error handling
            apiError = parseApiError(statusCode, responseBody);
        }
        else {
            // For non-JSON responses, create a basic ApiError
            apiError = new ApiError(statusCode);
        }
    }
    catch (e) {
        // If we can't parse the response, create a basic ApiError
        apiError = new ApiError(statusCode);
    }
    // Add additional context to the error for debugging
    const errorContext = {
        url: params.response.url,
        method: params.method,
        statusCode,
        interactionId,
        requestParams: params.reqParams,
    };
    // Log the error context for debugging
    console.error('API Request Failed:', errorContext);
    // Handle the error using our centralized error handler
    handleApiError(apiError);
    return apiError;
};
// Adds the specified prefix to all the object's fields
const addPrefix = (params) => {
    const acc = {};
    for (const key in params.object) {
        acc[`${params.prefix}.${key}`] = JSON.stringify(params.object[key]);
    }
    return acc;
};
// stringiflies the object to prevent the filtering of the nested elements
const stringifyElements = (object) => {
    const acc = {};
    for (const key in object) {
        acc[key] = JSON.stringify(object[key]);
    }
    return acc;
};
export { RestClient, UntypedRestClient };
